import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { useAnalytics, useToast } from 'hooks'
import { useCallback, useEffect, useRef, useState } from 'react'

/** How a referral was shared — matches the `REFERRAL_SHARED` analytics contract. */
export type ReferralShareMethod = 'copy_link' | 'native_share' | ReferralShareChannel

/** Social/email channels with a share-intent URL. */
export type ReferralShareChannel = 'email' | 'facebook' | 'x' | 'linkedin'

/** Where a share action was triggered from — matches the analytics contract. */
export type ReferralSurface = 'settings' | 'post_publish_success' | 'credits_exhausted'

interface ReferralSharedProperties {
  method: ReferralShareMethod
  surface: ReferralSurface
}

const REFERRAL_SHARED_EVENT = AnalyticsEvents.Referrals.REFERRAL_SHARED

/**
 * Email subject / native-share title. Mirrors the /invite landing page's OG
 * headline ("A friend gave you N free credits") so the story stays consistent
 * from message to click to landing. Falls back to a plain title when the credit
 * amount isn't known.
 */
const buildShareTitle = (refereeCredits?: number): string =>
  refereeCredits && refereeCredits > 0
    ? `A friend gave you ${refereeCredits} Taka credits`
    : 'Try Taka'

/**
 * The pitch that travels with the link on Email and X. Facebook and LinkedIn
 * ignore any prefilled text (their share dialogs only accept a URL), so this
 * only shows on the two channels that honor it. Lead with the payoff —
 * `refereeCredits` is the free credits the friend gets on signup.
 */
const buildShareText = (refereeCredits?: number): string =>
  refereeCredits && refereeCredits > 0
    ? `${refereeCredits} free credits for Taka, the AI that writes, designs, and schedules your social posts. Try it:`
    : 'Taka writes, designs, and schedules your social posts. Try it with my link:'

/**
 * Share-intent URL per channel. Facebook and LinkedIn only accept a URL —
 * they pull title/preview from the link's OG tags; the message text only
 * travels on email and X.
 */
const shareIntentUrl = (
  channel: ReferralShareChannel,
  link: string,
  shareText: string,
  subject: string
): string => {
  const url = encodeURIComponent(link)
  switch (channel) {
    case 'email':
      return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`${shareText} ${link}`)}`
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${url}`
    case 'x':
      return `https://x.com/intent/post?text=${encodeURIComponent(shareText)}&url=${url}`
    case 'linkedin':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${url}`
  }
}

/** True when the browser exposes the Web Share API (mobile / some desktop). */
export const canNativeShare = (): boolean =>
  typeof navigator !== 'undefined' && typeof navigator.share === 'function'

/** How long the "Copied ✓" confirmation stays lit after a copy. */
const COPIED_INDICATOR_MS = 1600

/**
 * Copy / native-share actions for a referral link, wired to the
 * `REFERRAL_SHARED` analytics event with a `surface` so each entry point
 * (settings, post-publish, credits-exhausted) is attributable. Shared across
 * every surface so copy, the transient `copied` indicator, toasts, and
 * tracking stay identical.
 */
export function useReferralShare(surface: ReferralSurface, refereeCredits?: number) {
  const { track } = useAnalytics()
  const { showSuccess, showError } = useToast()
  const shareText = buildShareText(refereeCredits)
  const shareTitle = buildShareTitle(refereeCredits)

  // Transient "Copied ✓" indicator owned here so every surface renders it the
  // same way (and the reset timer can't leak past unmount).
  const [copied, setCopied] = useState(false)
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(copiedTimer.current), [])

  const copyLink = useCallback(
    async (link: string) => {
      try {
        await navigator.clipboard.writeText(link)
        showSuccess('Referral link copied')
        setCopied(true)
        clearTimeout(copiedTimer.current)
        copiedTimer.current = setTimeout(() => setCopied(false), COPIED_INDICATOR_MS)
        track(REFERRAL_SHARED_EVENT, {
          method: 'copy_link',
          surface,
        } satisfies ReferralSharedProperties)
      } catch (error) {
        console.error('Failed to copy referral link:', error)
        showError('Could not copy the link')
      }
    },
    [surface, track, showSuccess, showError]
  )

  const nativeShare = useCallback(
    async (link: string) => {
      if (!canNativeShare()) return
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: link })
        track(REFERRAL_SHARED_EVENT, {
          method: 'native_share',
          surface,
        } satisfies ReferralSharedProperties)
      } catch (error) {
        // AbortError = user dismissed the share sheet; not a failure.
        if (error instanceof Error && error.name === 'AbortError') return
        console.error('Failed to share referral link:', error)
      }
    },
    [surface, track, shareText, shareTitle]
  )

  const shareVia = useCallback(
    (channel: ReferralShareChannel, link: string) => {
      const url = shareIntentUrl(channel, link, shareText, shareTitle)
      if (channel === 'email') {
        // mailto in window.open leaves a blank tab behind on some browsers.
        window.location.href = url
      } else {
        window.open(url, '_blank', 'noopener,noreferrer,width=640,height=640')
      }
      track(REFERRAL_SHARED_EVENT, {
        method: channel,
        surface,
      } satisfies ReferralSharedProperties)
    },
    [surface, track, shareText, shareTitle]
  )

  return { copied, copyLink, nativeShare, shareVia }
}
